import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Stack,
} from '@mui/joy';
import {
  Sparkle as AIIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../../lib/api';

interface SEOData {
  title?: string;
  description?: string;
  keywords?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  twitter_title?: string;
  twitter_description?: string;
  twitter_image?: string;
  canonical_url?: string;
}

interface SEOTabProps {
  seo: SEOData;
  onChange: (field: string, value: string) => void;
  aiSettings?: any;
}

export const SEOTab: React.FC<SEOTabProps> = ({ seo, onChange, aiSettings }) => {
  const [generating, setGenerating] = useState<string | null>(null);

  // AI generation mutation
  const generateMutation = useMutation({
    mutationFn: async (field: string) => {
      return api.post('/api/cms/ai/generate-seo/', {
        field,
        current_seo: seo,
        ai_settings: aiSettings,
      });
    },
    onSuccess: (response: any, field: string) => {
      const data = response?.data || response;
      if (data?.value) {
        onChange(field, data.value);
      }
    },
  });

  const handleGenerate = async (field: string) => {
    setGenerating(field);
    try {
      await generateMutation.mutateAsync(field);
    } finally {
      setGenerating(null);
    }
  };

  const handleGenerateAll = async () => {
    setGenerating('all');
    try {
      const response = await api.post('/api/cms/ai/generate-seo/', {
        field: 'all',
        current_seo: seo,
        ai_settings: aiSettings,
      });
      
      const seoData = (response as any)?.data?.seo || (response as any)?.seo;
      if (seoData) {
        Object.entries(seoData).forEach(([key, value]) => {
          onChange(key, value as string);
        });
      }
    } finally {
      setGenerating(null);
    }
  };

  const getCharCount = (text?: string) => {
    return text ? text.length : 0;
  };

  const getCharCountColor = (count: number, recommended: number, max: number) => {
    if (count === 0) return 'neutral';
    if (count > max) return 'danger';
    if (count < recommended * 0.8) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography level="h3">SEO Settings</Typography>
        <Button
          startDecorator={<AIIcon />}
          onClick={handleGenerateAll}
          loading={generating === 'all'}
        >
          Generate All SEO
        </Button>
      </Box>

      <Stack spacing={3}>
        {/* Basic SEO */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>Basic SEO</Typography>
            
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>
                  Page Title
                  <Chip
                    size="sm"
                    color={getCharCountColor(getCharCount(seo.title), 50, 60)}
                    sx={{ ml: 1 }}
                  >
                    {getCharCount(seo.title)}/60
                  </Chip>
                </FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Input
                    value={seo.title || ''}
                    onChange={(e) => onChange('title', e.target.value)}
                    placeholder="Enter page title (50-60 characters)"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    onClick={() => handleGenerate('title')}
                    loading={generating === 'title'}
                  >
                    <AIIcon />
                  </IconButton>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>
                  Meta Description
                  <Chip
                    size="sm"
                    color={getCharCountColor(getCharCount(seo.description), 150, 160)}
                    sx={{ ml: 1 }}
                  >
                    {getCharCount(seo.description)}/160
                  </Chip>
                </FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Textarea
                    value={seo.description || ''}
                    onChange={(e) => onChange('description', e.target.value)}
                    placeholder="Enter meta description (150-160 characters)"
                    minRows={2}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    onClick={() => handleGenerate('description')}
                    loading={generating === 'description'}
                  >
                    <AIIcon />
                  </IconButton>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>Keywords</FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Input
                    value={seo.keywords || ''}
                    onChange={(e) => onChange('keywords', e.target.value)}
                    placeholder="Enter keywords separated by commas"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    onClick={() => handleGenerate('keywords')}
                    loading={generating === 'keywords'}
                  >
                    <AIIcon />
                  </IconButton>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>Canonical URL (Optional)</FormLabel>
                <Input
                  value={seo.canonical_url || ''}
                  onChange={(e) => onChange('canonical_url', e.target.value)}
                  placeholder="https://example.com/page"
                />
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {/* Open Graph */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>Open Graph (Facebook)</Typography>
            
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>OG Title (Optional)</FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Input
                    value={seo.og_title || ''}
                    onChange={(e) => onChange('og_title', e.target.value)}
                    placeholder="Leave empty to use page title"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    onClick={() => handleGenerate('og_title')}
                    loading={generating === 'og_title'}
                  >
                    <AIIcon />
                  </IconButton>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>OG Description (Optional)</FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Textarea
                    value={seo.og_description || ''}
                    onChange={(e) => onChange('og_description', e.target.value)}
                    placeholder="Leave empty to use meta description"
                    minRows={2}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    onClick={() => handleGenerate('og_description')}
                    loading={generating === 'og_description'}
                  >
                    <AIIcon />
                  </IconButton>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>OG Image URL</FormLabel>
                <Input
                  value={seo.og_image || ''}
                  onChange={(e) => onChange('og_image', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {/* Twitter Card */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>Twitter Card</Typography>
            
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Twitter Title (Optional)</FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Input
                    value={seo.twitter_title || ''}
                    onChange={(e) => onChange('twitter_title', e.target.value)}
                    placeholder="Leave empty to use page title"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    onClick={() => handleGenerate('twitter_title')}
                    loading={generating === 'twitter_title'}
                  >
                    <AIIcon />
                  </IconButton>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>Twitter Description (Optional)</FormLabel>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Textarea
                    value={seo.twitter_description || ''}
                    onChange={(e) => onChange('twitter_description', e.target.value)}
                    placeholder="Leave empty to use meta description"
                    minRows={2}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    onClick={() => handleGenerate('twitter_description')}
                    loading={generating === 'twitter_description'}
                  >
                    <AIIcon />
                  </IconButton>
                </Box>
              </FormControl>

              <FormControl>
                <FormLabel>Twitter Image URL</FormLabel>
                <Input
                  value={seo.twitter_image || ''}
                  onChange={(e) => onChange('twitter_image', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {/* SEO Preview */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>Search Engine Preview</Typography>
            
            <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
              <Typography 
                level="body-lg" 
                sx={{ 
                  color: '#1a0dab',
                  mb: 0.5,
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                {seo.title || 'Page Title'}
              </Typography>
              <Typography level="body-sm" sx={{ color: '#006621', mb: 0.5 }}>
                https://example.com/{seo.canonical_url?.split('/').pop() || 'page'}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                {seo.description || 'Page description will appear here...'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};